// pages/admin/PaymentSettings.jsx - Updated for Stripe
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Switch,
  TextField,
  Button,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  Alert,
  Snackbar,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Payment as PaymentIcon,
  Settings as SettingsIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CreditCard as CreditCardIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import api from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const PaymentSettings = () => {
  const { language } = useLanguage();
  const [settings, setSettings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openRefundDialog, setOpenRefundDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [refundData, setRefundData] = useState({ amount: '', reason: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0
  });

  // Stripe-specific form state
  const [formData, setFormData] = useState({
    gateway_name: 'stripe',
    is_enabled: false,
    is_test_mode: true,
    publishable_key: '',
    secret_key: '',
    webhook_secret: '',
    display_name: 'Credit / Debit Card',
    display_name_ar: 'بطاقة ائتمان / خصم',
    display_icon: '💳',
    sort_order: 10,
    transaction_fee_percent: 2.9, // Stripe's typical fee
    transaction_fee_fixed: 0.3,    // Stripe's fixed fee
    min_amount: 1,
    max_amount: 10000,
    supported_currencies: ['MYR', 'USD'],
    config: {
      capture_method: 'automatic', // automatic or manual
      statement_descriptor: 'RETO Cafe',
      requires_cvv: true
    }
  });

  // Fetch payment settings
  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/payments/admin/settings');
      setSettings(response.data.settings || []);
    } catch (error) {
      console.error('Error fetching payment settings:', error);
      showSnackbar(
        language === 'ar' ? 'فشل في تحميل إعدادات الدفع' : 'Failed to load payment settings',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch transactions
  const fetchTransactions = async (page = 1) => {
    setTransactionsLoading(true);
    try {
      const response = await api.get('/payments/admin/transactions', {
        params: { page, limit: 10 }
      });
      setTransactions(response.data.transactions || []);
      setPagination(response.data.pagination || {
        currentPage: 1,
        totalPages: 1,
        totalCount: 0
      });
    } catch (error) {
      console.error('Error fetching transactions:', error);
      showSnackbar(
        language === 'ar' ? 'فشل في تحميل المعاملات' : 'Failed to load transactions',
        'error'
      );
    } finally {
      setTransactionsLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchTransactions();
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('config.')) {
      const configKey = name.replace('config.', '');
      setFormData(prev => ({
        ...prev,
        config: {
          ...prev.config,
          [configKey]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleArrayInputChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      supported_currencies: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const resetForm = () => {
    setFormData({
      gateway_name: 'stripe',
      is_enabled: false,
      is_test_mode: true,
      publishable_key: '',
      secret_key: '',
      webhook_secret: '',
      display_name: 'Credit / Debit Card',
      display_name_ar: 'بطاقة ائتمان / خصم',
      display_icon: '💳',
      sort_order: 10,
      transaction_fee_percent: 2.9,
      transaction_fee_fixed: 0.3,
      min_amount: 1,
      max_amount: 10000,
      supported_currencies: ['MYR', 'USD'],
      config: {
        capture_method: 'automatic',
        statement_descriptor: 'RETO Cafe',
        requires_cvv: true
      }
    });
    setEditingSetting(null);
  };

  const handleOpenDialog = (setting = null) => {
    if (setting) {
      setEditingSetting(setting);
      setFormData({
        gateway_name: setting.gateway_name,
        is_enabled: setting.is_enabled,
        is_test_mode: setting.is_test_mode,
        publishable_key: setting.publishable_key || '',
        secret_key: setting.secret_key || '',
        webhook_secret: setting.webhook_secret || '',
        display_name: setting.display_name || 'Credit / Debit Card',
        display_name_ar: setting.display_name_ar || 'بطاقة ائتمان / خصم',
        display_icon: setting.display_icon || '💳',
        sort_order: setting.sort_order || 10,
        transaction_fee_percent: setting.transaction_fee_percent || 2.9,
        transaction_fee_fixed: setting.transaction_fee_fixed || 0.3,
        min_amount: setting.min_amount || 1,
        max_amount: setting.max_amount || 10000,
        supported_currencies: setting.supported_currencies || ['MYR', 'USD'],
        config: setting.config || {
          capture_method: 'automatic',
          statement_descriptor: 'RETO Cafe',
          requires_cvv: true
        }
      });
    } else {
      resetForm();
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
  };

  const handleSaveSetting = async () => {
    setLoading(true);
    try {
      // Transform form data to match backend expected format
      const apiData = {
        ...formData,
        api_key: formData.publishable_key,
        api_secret: formData.secret_key
      };
      
      const response = await api.post('/payments/admin/settings', apiData);
      
      showSnackbar(
        language === 'ar' 
          ? (editingSetting ? 'تم تحديث الإعدادات بنجاح' : 'تم إضافة الإعدادات بنجاح')
          : (editingSetting ? 'Settings updated successfully' : 'Settings added successfully'),
        'success'
      );
      
      fetchSettings();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving payment setting:', error);
      showSnackbar(
        language === 'ar' ? 'فشل في حفظ الإعدادات' : 'Failed to save settings',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (setting) => {
    setEditingSetting(setting);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    try {
      await api.delete(`/payments/admin/settings/${editingSetting.id}`);
      
      showSnackbar(
        language === 'ar' ? 'تم حذف الإعدادات بنجاح' : 'Settings deleted successfully',
        'success'
      );
      
      fetchSettings();
      setOpenDeleteDialog(false);
      setEditingSetting(null);
    } catch (error) {
      console.error('Error deleting payment setting:', error);
      showSnackbar(
        language === 'ar' ? 'فشل في حذف الإعدادات' : 'Failed to delete settings',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (setting) => {
    try {
      const updatedSetting = {
        ...setting,
        is_enabled: !setting.is_enabled
      };
      
      await api.post('/payments/admin/settings', updatedSetting);
      
      showSnackbar(
        language === 'ar' 
          ? `تم ${!setting.is_enabled ? 'تفعيل' : 'تعطيل'} طريقة الدفع`
          : `Payment method ${!setting.is_enabled ? 'enabled' : 'disabled'}`,
        'success'
      );
      
      fetchSettings();
    } catch (error) {
      console.error('Error toggling payment status:', error);
      showSnackbar(
        language === 'ar' ? 'فشل في تغيير حالة الدفع' : 'Failed to toggle payment status',
        'error'
      );
    }
  };

  const handleViewTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setOpenDetailsDialog(true);
  };

  const handleCloseTransaction = () => {
    setSelectedTransaction(null);
    setOpenDetailsDialog(false);
  };

  const handleRefundClick = (transaction) => {
    setSelectedTransaction(transaction);
    setRefundData({
      amount: transaction.amount,
      reason: ''
    });
    setOpenRefundDialog(true);
  };

  const handleRefundConfirm = async () => {
    setLoading(true);
    try {
      await api.post(`/payments/admin/transactions/${selectedTransaction.id}/refund`, refundData);
      
      showSnackbar(
        language === 'ar' ? 'تم إجراء الاسترداد بنجاح' : 'Refund processed successfully',
        'success'
      );
      
      fetchTransactions(pagination.currentPage);
      setOpenRefundDialog(false);
      setSelectedTransaction(null);
      setRefundData({ amount: '', reason: '' });
    } catch (error) {
      console.error('Error processing refund:', error);
      showSnackbar(
        language === 'ar' ? 'فشل في إجراء الاسترداد' : 'Failed to process refund',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'succeeded': return '#4CAF50';
      case 'pending': return '#FFD700';
      case 'requires_payment_method': return '#FF9800';
      case 'requires_confirmation': return '#FF9800';
      case 'requires_action': return '#FF9800';
      case 'processing': return '#2196F3';
      case 'requires_capture': return '#9C27B0';
      case 'canceled': return '#F44336';
      case 'failed': return '#F44336';
      case 'refunded': return '#9C27B0';
      case 'partially_refunded': return '#FF9800';
      default: return '#B3B3B3';
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      succeeded: language === 'ar' ? 'مكتمل' : 'Succeeded',
      pending: language === 'ar' ? 'قيد الانتظار' : 'Pending',
      requires_payment_method: language === 'ar' ? 'يحتاج لطريقة دفع' : 'Requires Payment Method',
      requires_confirmation: language === 'ar' ? 'يحتاج لتأكيد' : 'Requires Confirmation',
      requires_action: language === 'ar' ? 'يحتاج لإجراء' : 'Requires Action',
      processing: language === 'ar' ? 'جاري المعالجة' : 'Processing',
      requires_capture: language === 'ar' ? 'يحتاج للتقاط' : 'Requires Capture',
      canceled: language === 'ar' ? 'ملغي' : 'Canceled',
      failed: language === 'ar' ? 'فشل' : 'Failed',
      refunded: language === 'ar' ? 'مسترجع' : 'Refunded',
      partially_refunded: language === 'ar' ? 'مسترجع جزئياً' : 'Partially Refunded'
    };
    return statusMap[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString(language === 'ar' ? 'ar-EG' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount, currency = 'MYR') => {
    return new Intl.NumberFormat(language === 'ar' ? 'ar-EG' : 'en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4, mb: 10, backgroundColor: '#000000', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PaymentIcon sx={{ fontSize: 40, color: '#FFD700' }} />
          <Typography variant="h4" sx={{ color: '#FFD700', fontWeight: 900 }}>
            {language === 'ar' ? 'إعدادات الدفع' : 'Payment Settings'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            backgroundColor: '#FFD700',
            color: '#000000',
            fontWeight: 700,
            '&:hover': { backgroundColor: '#FFE55C' }
          }}
        >
          {language === 'ar' ? 'إضافة طريقة دفع' : 'Add Payment Method'}
        </Button>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3, backgroundColor: '#0D0D0D', border: '1px solid #2D2D2D' }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: '1px solid #2D2D2D' }}>
          <Tab 
            label={language === 'ar' ? 'طرق الدفع' : 'Payment Methods'} 
            icon={<SettingsIcon />} 
            iconPosition="start"
            sx={{ color: tabValue === 0 ? '#FFD700' : '#B3B3B3' }}
          />
          <Tab 
            label={language === 'ar' ? 'المعاملات' : 'Transactions'} 
            icon={<CreditCardIcon />} 
            iconPosition="start"
            sx={{ color: tabValue === 1 ? '#FFD700' : '#B3B3B3' }}
          />
        </Tabs>
      </Paper>

      {/* Payment Methods Tab */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 8 }}>
              <CircularProgress sx={{ color: '#FFD700' }} />
            </Box>
          ) : settings.length === 0 ? (
            <Box sx={{ width: '100%', textAlign: 'center', py: 8 }}>
              <PaymentIcon sx={{ fontSize: 60, color: '#2D2D2D', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#B3B3B3' }}>
                {language === 'ar' ? 'لا توجد طرق دفع مضافة' : 'No payment methods added'}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => handleOpenDialog()}
                sx={{ mt: 2, borderColor: '#FFD700', color: '#FFD700' }}
              >
                {language === 'ar' ? 'إضافة طريقة دفع' : 'Add Payment Method'}
              </Button>
            </Box>
          ) : (
            settings.map((setting) => (
              <Grid item xs={12} key={setting.id}>
                <Card sx={{ 
                  backgroundColor: '#0D0D0D',
                  border: setting.is_enabled ? '1px solid #4CAF50' : '1px solid #2D2D2D',
                  borderRadius: '16px'
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h5" sx={{ fontSize: '2rem' }}>
                          {setting.display_icon || '💳'}
                        </Typography>
                        <Box>
                          <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 700 }}>
                            {language === 'ar' ? setting.display_name_ar : setting.display_name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                            {setting.gateway_name === 'stripe' ? 'Stripe' : setting.gateway_name} • {language === 'ar' ? 'مرتب' : 'Order'} {setting.sort_order}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Chip
                          label={setting.is_enabled 
                            ? (language === 'ar' ? 'مفعل' : 'Enabled')
                            : (language === 'ar' ? 'معطل' : 'Disabled')
                          }
                          sx={{
                            backgroundColor: setting.is_enabled ? '#4CAF50' : '#2D2D2D',
                            color: setting.is_enabled ? '#FFFFFF' : '#B3B3B3'
                          }}
                        />
                        <Chip
                          label={setting.is_test_mode 
                            ? (language === 'ar' ? 'وضع الاختبار' : 'Test Mode')
                            : (language === 'ar' ? 'وضع حقيقي' : 'Live Mode')
                          }
                          sx={{
                            backgroundColor: setting.is_test_mode ? '#FFD700' : '#2196F3',
                            color: setting.is_test_mode ? '#000000' : '#FFFFFF'
                          }}
                        />
                      </Box>
                    </Box>

                    <Divider sx={{ backgroundColor: '#2D2D2D', my: 2 }} />

                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 1 }}>
                          {language === 'ar' ? 'معلومات الدفع' : 'Payment Info'}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                              {language === 'ar' ? 'الحد الأدنى' : 'Min Amount'}:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                              {formatCurrency(setting.min_amount)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                              {language === 'ar' ? 'الحد الأقصى' : 'Max Amount'}:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                              {formatCurrency(setting.max_amount)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                              {language === 'ar' ? 'رسوم المعاملة' : 'Transaction Fee'}:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                              {setting.transaction_fee_percent}% + {formatCurrency(setting.transaction_fee_fixed)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                              {language === 'ar' ? 'العملات المدعومة' : 'Supported Currencies'}:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                              {setting.supported_currencies?.join(', ')}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 1 }}>
                          {language === 'ar' ? 'إعدادات Stripe' : 'Stripe Settings'}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                              Publishable Key:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#FFFFFF', fontFamily: 'monospace' }}>
                              {setting.publishable_key ? '••••••••' + setting.publishable_key.slice(-8) : '—'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                              Webhook Secret:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#FFFFFF', fontFamily: 'monospace' }}>
                              {setting.webhook_secret ? '••••••••' : '—'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                              {language === 'ar' ? 'طريقة التقاط' : 'Capture Method'}:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                              {setting.config?.capture_method === 'automatic' 
                                ? (language === 'ar' ? 'تلقائي' : 'Automatic')
                                : (language === 'ar' ? 'يدوي' : 'Manual')}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                              Statement Descriptor:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                              {setting.config?.statement_descriptor || 'RETO Cafe'}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={setting.is_enabled}
                            onChange={() => handleToggleStatus(setting)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#4CAF50',
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#4CAF50',
                              },
                            }}
                          />
                        }
                        label={language === 'ar' ? 'تفعيل' : 'Enable'}
                        sx={{ color: '#FFFFFF' }}
                      />
                      <Tooltip title={language === 'ar' ? 'تعديل' : 'Edit'}>
                        <IconButton onClick={() => handleOpenDialog(setting)} sx={{ color: '#FFD700' }}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={language === 'ar' ? 'حذف' : 'Delete'}>
                        <IconButton onClick={() => handleDeleteClick(setting)} sx={{ color: '#FF3D00' }}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      )}

      {/* Transactions Tab */}
      {tabValue === 1 && (
        <Paper sx={{ backgroundColor: '#0D0D0D', border: '1px solid #2D2D2D', borderRadius: '16px' }}>
          <Box sx={{ p: 3, borderBottom: '1px solid #2D2D2D' }}>
            <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700 }}>
              {language === 'ar' ? 'سجل المعاملات' : 'Transaction History'}
            </Typography>
          </Box>

          {transactionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: '#FFD700' }} />
            </Box>
          ) : transactions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CreditCardIcon sx={{ fontSize: 60, color: '#2D2D2D', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#B3B3B3' }}>
                {language === 'ar' ? 'لا توجد معاملات' : 'No transactions found'}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#1A1A1A' }}>
                    <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>
                      {language === 'ar' ? 'المعرف' : 'ID'}
                    </TableCell>
                    <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>
                      {language === 'ar' ? 'الطلب' : 'Order'}
                    </TableCell>
                    <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>
                      {language === 'ar' ? 'المبلغ' : 'Amount'}
                    </TableCell>
                    <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>
                      {language === 'ar' ? 'الحالة' : 'Status'}
                    </TableCell>
                    <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>
                      {language === 'ar' ? 'طريقة الدفع' : 'Method'}
                    </TableCell>
                    <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>
                      {language === 'ar' ? 'التاريخ' : 'Date'}
                    </TableCell>
                    <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>
                      {language === 'ar' ? 'إجراءات' : 'Actions'}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((transaction) => (
                    <TableRow key={transaction.id} sx={{ '&:hover': { backgroundColor: '#1A1A1A' } }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#FFFFFF', fontFamily: 'monospace' }}>
                          {transaction.transaction_id?.substring(0, 8)}...
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                          #{transaction.order_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#FFD700', fontWeight: 600 }}>
                          {formatCurrency(transaction.amount, transaction.currency)}
                        </Typography>
                        {transaction.fee_amount > 0 && (
                          <Typography variant="caption" sx={{ color: '#B3B3B3', display: 'block' }}>
                            {language === 'ar' ? 'رسوم' : 'Fee'}: {formatCurrency(transaction.fee_amount)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusText(transaction.status)}
                          size="small"
                          sx={{
                            backgroundColor: getStatusColor(transaction.status),
                            color: '#FFFFFF',
                            fontWeight: 600,
                            minWidth: 80
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{transaction.payment_setting?.display_icon || '💳'}</span>
                          <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                            {transaction.payment_method === 'stripe' ? 'Stripe' : transaction.payment_method}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                          {formatDate(transaction.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title={language === 'ar' ? 'عرض التفاصيل' : 'View Details'}>
                            <IconButton
                              size="small"
                              onClick={() => handleViewTransaction(transaction)}
                              sx={{ color: '#FFD700' }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {transaction.status === 'succeeded' && (
                            <Tooltip title={language === 'ar' ? 'استرداد' : 'Refund'}>
                              <IconButton
                                size="small"
                                onClick={() => handleRefundClick(transaction)}
                                sx={{ color: '#4CAF50' }}
                              >
                                <RefreshIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <Button
                disabled={pagination.currentPage === 1}
                onClick={() => fetchTransactions(pagination.currentPage - 1)}
                sx={{ color: pagination.currentPage === 1 ? '#6B6B6B' : '#FFD700' }}
              >
                {language === 'ar' ? 'السابق' : 'Previous'}
              </Button>
              <Typography sx={{ mx: 2, color: '#FFFFFF' }}>
                {pagination.currentPage} / {pagination.totalPages}
              </Typography>
              <Button
                disabled={pagination.currentPage === pagination.totalPages}
                onClick={() => fetchTransactions(pagination.currentPage + 1)}
                sx={{ color: pagination.currentPage === pagination.totalPages ? '#6B6B6B' : '#FFD700' }}
              >
                {language === 'ar' ? 'التالي' : 'Next'}
              </Button>
            </Box>
          )}
        </Paper>
      )}

      {/* Add/Edit Payment Method Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#0D0D0D',
            border: '1px solid #FFD700',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ color: '#FFD700', fontWeight: 700, borderBottom: '1px solid #2D2D2D' }}>
          {editingSetting
            ? (language === 'ar' ? 'تعديل إعدادات Stripe' : 'Edit Stripe Settings')
            : (language === 'ar' ? 'إضافة إعدادات Stripe جديدة' : 'Add New Stripe Settings')
          }
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Stripe Info Alert */}
            <Grid item xs={12}>
              <Alert 
                severity="info" 
                icon={<InfoIcon />}
                sx={{ 
                  mb: 2,
                  backgroundColor: 'rgba(255, 215, 0, 0.1)',
                  color: '#FFD700',
                  border: '1px solid #FFD700'
                }}
              >
                {language === 'ar' 
                  ? 'يمكنك الحصول على مفاتيح API من لوحة تحكم Stripe (Dashboard → Developers → API Keys)'
                  : 'Get your API keys from Stripe Dashboard (Dashboard → Developers → API Keys)'}
              </Alert>
            </Grid>

            {/* Basic Settings */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 2 }}>
                {language === 'ar' ? 'الإعدادات الأساسية' : 'Basic Settings'}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={language === 'ar' ? 'الاسم المعروض (عربي)' : 'Display Name (AR)'}
                name="display_name_ar"
                value={formData.display_name_ar}
                onChange={handleInputChange}
                InputProps={{ sx: { color: '#FFFFFF' } }}
                InputLabelProps={{ sx: { color: '#B3B3B3' } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={language === 'ar' ? 'الاسم المعروض (إنجليزي)' : 'Display Name (EN)'}
                name="display_name"
                value={formData.display_name}
                onChange={handleInputChange}
                InputProps={{ sx: { color: '#FFFFFF' } }}
                InputLabelProps={{ sx: { color: '#B3B3B3' } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={language === 'ar' ? 'الأيقونة' : 'Icon'}
                name="display_icon"
                value={formData.display_icon}
                onChange={handleInputChange}
                helperText={language === 'ar' ? 'استخدم رمز Emoji' : 'Use Emoji'}
                InputProps={{ sx: { color: '#FFFFFF' } }}
                InputLabelProps={{ sx: { color: '#B3B3B3' } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label={language === 'ar' ? 'ترتيب الظهور' : 'Sort Order'}
                name="sort_order"
                value={formData.sort_order}
                onChange={handleInputChange}
                InputProps={{ sx: { color: '#FFFFFF' } }}
                InputLabelProps={{ sx: { color: '#B3B3B3' } }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ backgroundColor: '#2D2D2D', my: 2 }} />
            </Grid>

            {/* Stripe API Settings */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 2 }}>
                Stripe API {language === 'ar' ? 'الإعدادات' : 'Settings'}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Publishable Key"
                name="publishable_key"
                value={formData.publishable_key}
                onChange={handleInputChange}
                placeholder="pk_test_..."
                InputProps={{ sx: { color: '#FFFFFF' } }}
                InputLabelProps={{ sx: { color: '#B3B3B3' } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Secret Key"
                name="secret_key"
                value={formData.secret_key}
                onChange={handleInputChange}
                type="password"
                placeholder="sk_test_..."
                InputProps={{ sx: { color: '#FFFFFF' } }}
                InputLabelProps={{ sx: { color: '#B3B3B3' } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={language === 'ar' ? 'سر Webhook' : 'Webhook Secret'}
                name="webhook_secret"
                value={formData.webhook_secret}
                onChange={handleInputChange}
                type="password"
                placeholder="whsec_..."
                helperText={language === 'ar' ? 'من لوحة تحكم Stripe (Webhooks)' : 'From Stripe Dashboard (Webhooks)'}
                InputProps={{ sx: { color: '#FFFFFF' } }}
                InputLabelProps={{ sx: { color: '#B3B3B3' } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={language === 'ar' ? 'وصف كشف الحساب' : 'Statement Descriptor'}
                name="config.statement_descriptor"
                value={formData.config.statement_descriptor}
                onChange={handleInputChange}
                placeholder="RETO Cafe"
                helperText={language === 'ar' ? 'يظهر في كشف حساب العميل' : 'Appears on customer statement'}
                InputProps={{ sx: { color: '#FFFFFF' } }}
                InputLabelProps={{ sx: { color: '#B3B3B3' } }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ backgroundColor: '#2D2D2D', my: 2 }} />
            </Grid>

            {/* Fee Settings */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 2 }}>
                {language === 'ar' ? 'إعدادات الرسوم' : 'Fee Settings'}
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label={language === 'ar' ? 'نسبة الرسوم %' : 'Fee Percentage %'}
                name="transaction_fee_percent"
                value={formData.transaction_fee_percent}
                onChange={handleInputChange}
                InputProps={{ sx: { color: '#FFFFFF' } }}
                InputLabelProps={{ sx: { color: '#B3B3B3' } }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label={language === 'ar' ? 'رسوم ثابتة' : 'Fixed Fee'}
                name="transaction_fee_fixed"
                value={formData.transaction_fee_fixed}
                onChange={handleInputChange}
                InputProps={{ sx: { color: '#FFFFFF' } }}
                InputLabelProps={{ sx: { color: '#B3B3B3' } }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label={language === 'ar' ? 'الحد الأدنى' : 'Min Amount'}
                name="min_amount"
                value={formData.min_amount}
                onChange={handleInputChange}
                InputProps={{ sx: { color: '#FFFFFF' } }}
                InputLabelProps={{ sx: { color: '#B3B3B3' } }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label={language === 'ar' ? 'الحد الأقصى' : 'Max Amount'}
                name="max_amount"
                value={formData.max_amount}
                onChange={handleInputChange}
                InputProps={{ sx: { color: '#FFFFFF' } }}
                InputLabelProps={{ sx: { color: '#B3B3B3' } }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#B3B3B3' }}>
                  {language === 'ar' ? 'طريقة التقاط' : 'Capture Method'}
                </InputLabel>
                <Select
                  name="config.capture_method"
                  value={formData.config.capture_method}
                  onChange={handleInputChange}
                  sx={{ color: '#FFFFFF' }}
                >
                  <MenuItem value="automatic">
                    {language === 'ar' ? 'تلقائي' : 'Automatic'}
                  </MenuItem>
                  <MenuItem value="manual">
                    {language === 'ar' ? 'يدوي' : 'Manual'}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#B3B3B3' }}>
                  {language === 'ar' ? 'العملات المدعومة' : 'Supported Currencies'}
                </InputLabel>
                <Select
                  multiple
                  name="supported_currencies"
                  value={formData.supported_currencies}
                  onChange={handleArrayInputChange}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip 
                          key={value} 
                          label={value} 
                          size="small" 
                          sx={{ backgroundColor: '#FFD700', color: '#000000' }} 
                        />
                      ))}
                    </Box>
                  )}
                  sx={{ color: '#FFFFFF' }}
                >
                  <MenuItem value="MYR">MYR</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ backgroundColor: '#2D2D2D', my: 2 }} />
            </Grid>

            {/* Additional Settings */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 2 }}>
                {language === 'ar' ? 'إعدادات إضافية' : 'Additional Settings'}
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_enabled}
                    onChange={handleInputChange}
                    name="is_enabled"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#4CAF50',
                      },
                    }}
                  />
                }
                label={language === 'ar' ? 'مفعل' : 'Enabled'}
                sx={{ color: '#FFFFFF' }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_test_mode}
                    onChange={handleInputChange}
                    name="is_test_mode"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#FFD700',
                      },
                    }}
                  />
                }
                label={language === 'ar' ? 'وضع الاختبار' : 'Test Mode'}
                sx={{ color: '#FFFFFF' }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.config?.requires_cvv}
                    onChange={handleInputChange}
                    name="config.requires_cvv"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#FFD700',
                      },
                    }}
                  />
                }
                label={language === 'ar' ? 'يتطلب CVV' : 'Requires CVV'}
                sx={{ color: '#FFFFFF' }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: '1px solid #2D2D2D' }}>
          <Button onClick={handleCloseDialog} sx={{ color: '#B3B3B3' }}>
            {language === 'ar' ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button
            onClick={handleSaveSetting}
            variant="contained"
            disabled={loading}
            startIcon={<SaveIcon />}
            sx={{
              backgroundColor: '#FFD700',
              color: '#000000',
              '&:hover': { backgroundColor: '#FFE55C' }
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: '#000000' }} />
            ) : (
              language === 'ar' ? 'حفظ' : 'Save'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transaction Details Dialog */}
      <Dialog
        open={openDetailsDialog}
        onClose={handleCloseTransaction}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#0D0D0D',
            border: '1px solid #FFD700',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ color: '#FFD700', fontWeight: 700, borderBottom: '1px solid #2D2D2D' }}>
          {language === 'ar' ? 'تفاصيل المعاملة' : 'Transaction Details'}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedTransaction && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                  {language === 'ar' ? 'رقم المعاملة' : 'Transaction ID'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {selectedTransaction.transaction_id}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                  {language === 'ar' ? 'المرجع' : 'Reference'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF', fontFamily: 'monospace' }}>
                  {selectedTransaction.reference}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                  {language === 'ar' ? 'المبلغ' : 'Amount'}
                </Typography>
                <Typography variant="h6" sx={{ color: '#FFD700' }}>
                  {formatCurrency(selectedTransaction.amount, selectedTransaction.currency)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                  {language === 'ar' ? 'الحالة' : 'Status'}
                </Typography>
                <Chip
                  label={getStatusText(selectedTransaction.status)}
                  size="small"
                  sx={{
                    backgroundColor: getStatusColor(selectedTransaction.status),
                    color: '#FFFFFF',
                    fontWeight: 600
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ backgroundColor: '#2D2D2D', my: 1 }} />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                  {language === 'ar' ? 'الرسوم' : 'Fee'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                  {formatCurrency(selectedTransaction.fee_amount, selectedTransaction.currency)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                  {language === 'ar' ? 'الصافي' : 'Net'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                  {formatCurrency(selectedTransaction.net_amount, selectedTransaction.currency)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                  {language === 'ar' ? 'تاريخ الإنشاء' : 'Created At'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                  {formatDate(selectedTransaction.created_at)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                  {language === 'ar' ? 'تاريخ الدفع' : 'Paid At'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                  {selectedTransaction.paid_at ? formatDate(selectedTransaction.paid_at) : '—'}
                </Typography>
              </Grid>
              {selectedTransaction.error_message && (
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                    {language === 'ar' ? 'رسالة الخطأ' : 'Error Message'}
                  </Typography>
                  <Alert severity="error" sx={{ mt: 1, backgroundColor: '#1A1A1A' }}>
                    {selectedTransaction.error_message}
                  </Alert>
                </Grid>
              )}
              {selectedTransaction.gateway_response && (
                <Grid item xs={12}>
                  <Accordion sx={{ backgroundColor: '#1A1A1A', mt: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#FFD700' }} />}>
                      <Typography sx={{ color: '#FFD700' }}>
                        {language === 'ar' ? 'استجابة البوابة' : 'Gateway Response'}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="caption" sx={{ color: '#B3B3B3', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                        {JSON.stringify(selectedTransaction.gateway_response, null, 2)}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #2D2D2D' }}>
          <Button onClick={handleCloseTransaction} sx={{ color: '#B3B3B3' }}>
            {language === 'ar' ? 'إغلاق' : 'Close'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        PaperProps={{
          sx: {
            backgroundColor: '#0D0D0D',
            border: '1px solid #FF3D00',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ color: '#FF3D00', fontWeight: 700 }}>
          {language === 'ar' ? 'تأكيد الحذف' : 'Confirm Delete'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
            <WarningIcon sx={{ color: '#FF3D00', fontSize: 40 }} />
            <Typography sx={{ color: '#FFFFFF' }}>
              {language === 'ar' 
                ? 'هل أنت متأكد من حذف طريقة الدفع هذه؟'
                : 'Are you sure you want to delete this payment method?'
              }
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #2D2D2D' }}>
          <Button onClick={() => setOpenDeleteDialog(false)} sx={{ color: '#B3B3B3' }}>
            {language === 'ar' ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: '#FF3D00',
              color: '#FFFFFF',
              '&:hover': { backgroundColor: '#D32F2F' }
            }}
          >
            {loading ? <CircularProgress size={24} /> : (language === 'ar' ? 'حذف' : 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog
        open={openRefundDialog}
        onClose={() => setOpenRefundDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#0D0D0D',
            border: '1px solid #4CAF50',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ color: '#4CAF50', fontWeight: 700, borderBottom: '1px solid #2D2D2D' }}>
          {language === 'ar' ? 'استرداد المبلغ' : 'Refund Payment'}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedTransaction && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: '#B3B3B3', mb: 2 }}>
                  {language === 'ar' 
                    ? `إجمالي المبلغ القابل للاسترداد: ${formatCurrency(selectedTransaction.amount)}`
                    : `Total refundable amount: ${formatCurrency(selectedTransaction.amount)}`
                  }
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label={language === 'ar' ? 'المبلغ المراد استرداده' : 'Amount to Refund'}
                  value={refundData.amount}
                  onChange={(e) => setRefundData({ ...refundData, amount: e.target.value })}
                  InputProps={{
                    inputProps: { min: 0.01, max: selectedTransaction.amount },
                    sx: { color: '#FFFFFF' }
                  }}
                  InputLabelProps={{ sx: { color: '#B3B3B3' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label={language === 'ar' ? 'سبب الاسترداد' : 'Refund Reason'}
                  value={refundData.reason}
                  onChange={(e) => setRefundData({ ...refundData, reason: e.target.value })}
                  InputProps={{ sx: { color: '#FFFFFF' } }}
                  InputLabelProps={{ sx: { color: '#B3B3B3' } }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #2D2D2D' }}>
          <Button onClick={() => setOpenRefundDialog(false)} sx={{ color: '#B3B3B3' }}>
            {language === 'ar' ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button
            onClick={handleRefundConfirm}
            variant="contained"
            disabled={loading || !refundData.amount || refundData.amount <= 0}
            sx={{
              backgroundColor: '#4CAF50',
              color: '#FFFFFF',
              '&:hover': { backgroundColor: '#45a049' }
            }}
          >
            {loading ? <CircularProgress size={24} /> : (language === 'ar' ? 'استرداد' : 'Refund')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          sx={{
            backgroundColor: '#0D0D0D',
            color: '#FFFFFF',
            border: `1px solid ${snackbar.severity === 'success' ? '#4CAF50' : '#FF3D00'}`,
            '& .MuiAlert-icon': {
              color: snackbar.severity === 'success' ? '#4CAF50' : '#FF3D00'
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PaymentSettings;